import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import LivingBubble from '@/components/LivingBubble';
import { MagnifyingGlass, Sparkle, Heart } from '@phosphor-icons/react';
import SurpriseSparkButton from '@/components/SurpriseSparkButton';

const Discover = () => {
  const [bubbles, setBubbles] = useState([]);
  const [pulses, setPulses] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const [d, p] = await Promise.all([
        api.get('/discover'),
        api.get('/pulses/feed'),
      ]);
      setBubbles(d.data.bubbles || []);
      setPulses(p.data.pulses || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const follow = async (id) => {
    try {
      await api.post(`/bubbles/${id}/follow`);
      toast.success('following.');
    } catch { toast.error('could not'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan">discover · for-you</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Bubbles in motion</h1>
        <p className="text-white/60 mt-2 text-sm max-w-2xl">
          Nearby, popular, and freshly-reacted. Follow the ones you want in your Diary.
        </p>
        <div className="mt-4">
          <SurpriseSparkButton />
        </div>
      </div>

      {pulses.length > 0 && (
        <section className="mb-8">
          <div className="text-[10px] uppercase tracking-widest text-bx-volt mb-2">live pulses · 24h</div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {pulses.slice(0, 20).map((p) => (
              <Link key={p.id} to={`/bubble/${p.bubble_id}`}
                data-testid={`pulse-card-${p.id}`}
                className="shrink-0 glass rounded-2xl px-3 py-2 flex items-center gap-2 hover:bg-white/[0.07] transition">
                {p.emoji && <span className="text-lg">{p.emoji}</span>}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">{p.bubble_name}</div>
                  <div className="text-sm text-white/80 max-w-xs truncate">{p.text}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <div className="text-white/60">listening…</div>
      ) : bubbles.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <MagnifyingGlass size={28} className="text-bx-cyan mx-auto mb-2" />
          <div className="text-white/65">Quiet out there. Try checking in on Compass first.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {bubbles.map((b) => {
            return (
              <div key={b.id} data-testid={`discover-bubble-${b.id}`}
                className="glass rounded-2xl p-3 flex flex-col items-center gap-2">
                <Link to={`/bubble/${b.id}`} className="block">
                  <div className="h-20 w-20 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/20">
                    <LivingBubble bubble={b} mood="curious" className="h-full w-full" testid={`discover-living-${b.id}`} />
                  </div>
                </Link>
                <div className="text-sm font-bold">{b.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">L{b.level || 1} · {b.rarity || 'common'}</div>
                <button
                  data-testid={`follow-${b.id}`}
                  onClick={() => follow(b.id)}
                  className="text-[10px] px-3 py-1 rounded-full bg-bx-cyan/15 hover:bg-bx-cyan/25 text-bx-cyan font-bold uppercase tracking-widest">
                  follow
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Discover;
